"use client";

import { useState } from "react";
import {
  Search, Plus, Edit, Trash2, FileText, AlertTriangle,
  IndianRupee, Clock, ChevronUp, ChevronDown, ArrowUpDown,
  List, Undo2, ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import Pagination from "../../Common/Pagination";
import VendorProductsTab from "./VendorProductsTab";
import VendorReportsTab from "./VendorReportsTab";
import { useVendors, BUSINESS_TYPES } from "./hooks/useVendors";

const VendorManagement = () => {
  const {
    vendors, loading, error, message, setMessage,
    selectedVendor, setSelectedVendor,
    searchTerm, setSearchTerm,
    showAddForm, setShowAddForm,
    showVendorForm, setShowVendorForm,
    currentView, setCurrentView,
    filterOptions, setFilterOptions,
    editingVendor, setEditingVendor,
    showEditForm, setShowEditForm,
    sortColumn, sortDirection,
    currentPage, setCurrentPage,
    itemsPerPage, setItemsPerPage,
    productSortField, productSortDirection,
    newVendor, setNewVendor,
    newProduct, setNewProduct,
    newTransaction, setNewTransaction,
    showTransactionForm, setShowTransactionForm,
    selectedVendorForTransaction, setSelectedVendorForTransaction,
    transactionId,
    vendorErrors, setVendorErrors,
    editVendorErrors, setEditVendorErrors,
    pendingDelete, undoDelete,
    deleteConfirmVendor, setDeleteConfirmVendor, initiateDelete,
    editingTransaction, setEditingTransaction,
    showEditTransactionForm, setShowEditTransactionForm,
    txModalVendorId, showTxModal, setShowTxModal,
    txFilter, setTxFilter,
    filteredVendors, paginatedVendors,
    fetchVendors,
    handleEditVendor, handleAddVendor, handleAddProduct,
    handleAddTransaction, toggleVendorStatus, handleDeleteVendor,
    updateTransaction, openTxModal,
    handleVendorSort, handleProductSort,
    openVendorAgreement, openRecordPurchaseForVendor,
    getFilteredProducts, getReportData, getAlerts, getVendorPerformance,
    getStatusBadgeClass,
    updateTransactionStatus,
  } = useVendors();

  const [txErrors, setTxErrors] = useState({});

  const txModalVendor = vendors.find((v) => v.id === txModalVendorId);
  const filteredTxList = txFilter === "all"
    ? (txModalVendor?.transactions || [])
    : (txModalVendor?.transactions || []).filter((t) => t.status === txFilter);

  const getVendorSortIcon = (column) => {
    if (column !== sortColumn) return <ArrowUpDown className="h-4 w-4 text-gray-400 ml-1" />;
    return sortDirection === "asc"
      ? <ChevronUp className="h-4 w-4 text-[#0263cc] ml-1" />
      : <ChevronDown className="h-4 w-4 text-[#0263cc] ml-1" />;
  };

  if (loading && !vendors.length) {
    return (
      <div className="p-6 flex justify-center items-center h-screen">
        <div className="flex items-center space-x-2">
          <Clock className="animate-spin h-5 w-5 text-[#0263cc]" />
          <span className="text-gray-600">Loading data...</span>
        </div>
      </div>
    );
  }

  if (error && !vendors.length) {
    return (
      <div className="p-6">
        <Alert variant="destructive" className="bg-red-50 text-red-800 border-red-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 mr-2" />
          <AlertDescription className="font-medium">{error}</AlertDescription>
        </Alert>
        <div className="mt-4 flex justify-center">
          <Button onClick={fetchVendors} className="bg-[#0263cc] hover:bg-[#0263cc]/90">Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="w-full mx-auto">
        <Tabs defaultValue={currentView} className="w-full" onValueChange={setCurrentView}>
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-white p-1 rounded-lg">
            <TabsTrigger value="vendors" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white">Vendors</TabsTrigger>
            <TabsTrigger value="reports" className="rounded-md data-[state=active]:bg-[#0263cc] data-[state=active]:text-white">Reports</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products">
            <VendorProductsTab
              vendors={vendors}
              selectedVendor={selectedVendor}
              setSelectedVendor={setSelectedVendor}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              message={message}
              getFilteredProducts={getFilteredProducts}
              getStatusBadgeClass={getStatusBadgeClass}
              showAddForm={showAddForm}
              setShowAddForm={setShowAddForm}
              newProduct={newProduct}
              setNewProduct={setNewProduct}
              handleAddProduct={handleAddProduct}
              productSortField={productSortField}
              productSortDirection={productSortDirection}
              handleProductSort={handleProductSort}
              loading={loading}
            />
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
              <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4">
                <CardTitle className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <span className="text-[#0263cc] text-xl font-semibold">Vendor Management</span>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <div className="relative w-full sm:w-auto">
                      <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search vendors..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 pr-4 py-2 bg-white border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg w-full sm:w-64"
                      />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Select value={filterOptions.businessType} onValueChange={(value) => setFilterOptions({ ...filterOptions, businessType: value })}>
                        <SelectTrigger className="w-full sm:w-auto border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                          <SelectValue placeholder="Business Type" />
                        </SelectTrigger>
                        <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                          <SelectItem value="all">All Business Types</SelectItem>
                          {BUSINESS_TYPES.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select value={filterOptions.status} onValueChange={(value) => setFilterOptions({ ...filterOptions, status: value })}>
                        <SelectTrigger className="w-full sm:w-auto border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc] rounded-lg">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent className="rounded-lg shadow-lg border-[#0263cc]/10">
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardTitle>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={() => setShowVendorForm(true)} className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white rounded-lg flex items-center gap-2" aria-label="Add vendor">
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Add Vendor</span>
                        <span className="sm:hidden">Add</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Register a new vendor</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button onClick={() => { setSelectedVendorForTransaction(null); setShowTransactionForm(true); }} className="bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2" aria-label="Add transaction">
                        <IndianRupee className="h-4 w-4" />
                        <span className="hidden sm:inline">Add Transaction</span>
                        <span className="sm:hidden">Transaction</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">Log a purchase or payment with a vendor</TooltipContent>
                  </Tooltip>
                </div>
                <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                  Orders and purchases are arranged with the vendor outside this app. Use <strong>Add Transaction</strong> (or the rupee icon on a row) to record amount, date, and delivery for your records.
                </p>
              </CardHeader>

              <CardContent className="p-0">
                {message.content && (
                  <Alert
                    variant={message.type === "error" ? "destructive" : "default"}
                    className={`m-4 rounded-lg flex items-center justify-between ${message.type === "success" ? "bg-green-50 text-green-800 border-green-200" : message.type === "error" ? "bg-red-50 text-red-800 border-red-200" : message.type === "undo" ? "bg-orange-50 text-orange-800 border-orange-200" : "bg-blue-50 text-blue-800 border-blue-200"}`}
                  >
                    <AlertDescription className="font-medium">{message.content}</AlertDescription>
                    {message.type === "undo" && pendingDelete && (
                      <Button size="sm" variant="outline" className="ml-4 border-orange-400 text-orange-800 hover:bg-orange-100 flex items-center gap-1 shrink-0" onClick={undoDelete}>
                        <Undo2 className="h-3 w-3" /> Undo
                      </Button>
                    )}
                  </Alert>
                )}

                {/* Mobile Vendor Cards */}
                <div className="block md:hidden space-y-4 p-4">
                  {filteredVendors.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">{searchTerm ? "No vendors found matching your search" : "No vendors available"}</div>
                  ) : (
                    paginatedVendors.map((vendor) => (
                      <div key={vendor.id} className="border rounded-lg p-4 bg-white shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-medium">{vendor.name}</h3>
                          <span className={getStatusBadgeClass(vendor.status)}>{vendor.status === "active" ? "Active" : "Inactive"}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-y-2 text-sm">
                          <div className="text-gray-500">Business Type:</div><div>{vendor.businessType}</div>
                          <div className="text-gray-500">Contact:</div><div>{vendor.contactDetails?.phone || "—"}</div>
                          <div className="text-gray-500">Email:</div><div className="truncate">{vendor.contactDetails?.email || "—"}</div>
                          <div className="text-gray-500">GST/Tax ID:</div><div>{vendor.gstId}</div>
                          <div className="text-gray-500">Transactions:</div><div>{vendor.transactions.length}</div>
                        </div>
                        {vendor.transactions && vendor.transactions.length > 0 && (
                          <div className="mt-3">
                            <button onClick={() => openTxModal(vendor)} className="text-sm text-[#0263cc] underline underline-offset-2 flex items-center gap-1">
                              <List className="h-3 w-3" /> View {vendor.transactions.length} transaction{vendor.transactions.length !== 1 ? "s" : ""}
                            </button>
                          </div>
                        )}
                        <div className="mt-3 flex justify-end flex-wrap gap-1">
                          {[
                            { icon: <Edit className="h-4 w-4" />, label: "Edit vendor details", class: "text-[#0263cc] hover:text-[#0263cc]/80", onClick: () => { const isStd = BUSINESS_TYPES.includes(vendor.businessType) && vendor.businessType !== "Other"; setEditingVendor({ ...vendor, businessType: isStd ? vendor.businessType : "Other", otherBusinessType: isStd ? "" : vendor.businessType }); setShowEditForm(true); } },
                            { icon: <IndianRupee className="h-4 w-4" />, label: "Record purchase / payment", class: "text-green-700 hover:text-green-800", onClick: () => openRecordPurchaseForVendor(vendor) },
                            { icon: <FileText className="h-4 w-4" />, label: "View agreement / contract document", class: "text-gray-600 hover:text-gray-800", onClick: () => openVendorAgreement(vendor) },
                          ].map(({ icon, label, class: cls, onClick }, i) => (
                            <Tooltip key={i}>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className={`${cls} p-1`} onClick={onClick}>{icon}</Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">{label}</TooltipContent>
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Desktop Vendor Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 border-y border-gray-200">
                        {[{ key: "name", label: "Name" }, { key: "businessType", label: "Business Type" }].map(({ key, label }) => (
                          <th key={key} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleVendorSort(key)}>
                            <div className="flex items-center">{label}{getVendorSortIcon(key)}</div>
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleVendorSort("gstId")}>
                          <div className="flex items-center">GST/Tax ID{getVendorSortIcon("gstId")}</div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Orders</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredVendors.length === 0 ? (
                        <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">{searchTerm ? "No vendors found matching your search" : "No vendors available"}</td></tr>
                      ) : (
                        paginatedVendors.map((vendor) => (
                          <tr key={vendor.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4"><div className="font-medium">{vendor.name}</div></td>
                            <td className="px-4 py-4">{vendor.businessType}</td>
                            <td className="px-4 py-4">
                              <div>{vendor.contactDetails?.phone || "—"}</div>
                              <div className="text-sm text-gray-600">{vendor.contactDetails?.email || "—"}</div>
                            </td>
                            <td className="px-4 py-4">{vendor.gstId}</td>
                            <td className="px-4 py-4">
                              <Button variant="ghost" size="sm" onClick={() => toggleVendorStatus(vendor.id)} className={getStatusBadgeClass(vendor.status)}>
                                {vendor.status === "active" ? "Active" : "Inactive"}
                              </Button>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm font-medium">{vendor.transactions.length} transaction{vendor.transactions.length !== 1 ? "s" : ""}</div>
                              {vendor.transactions.length > 0 && (
                                <button onClick={() => openTxModal(vendor)} className="text-xs text-[#0263cc] underline underline-offset-2 flex items-center gap-1 mt-1">
                                  <List className="h-3 w-3" /> View all
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex flex-wrap gap-1">
                                {[
                                  { icon: <Edit className="h-4 w-4" />, label: "Edit vendor details", class: "text-[#0263cc] hover:text-[#0263cc]/80", onClick: () => { const isStd = BUSINESS_TYPES.includes(vendor.businessType) && vendor.businessType !== "Other"; setEditingVendor({ ...vendor, businessType: isStd ? vendor.businessType : "Other", otherBusinessType: isStd ? "" : vendor.businessType }); setShowEditForm(true); } },
                                  { icon: <IndianRupee className="h-4 w-4" />, label: "Record purchase / payment (add transaction)", class: "text-green-700 hover:text-green-800", onClick: () => openRecordPurchaseForVendor(vendor) },
                                  { icon: <FileText className="h-4 w-4" />, label: "View agreement / contract document", class: "text-gray-600 hover:text-gray-800", onClick: () => openVendorAgreement(vendor) },
                                ].map(({ icon, label, class: cls, onClick }, i) => (
                                  <Tooltip key={i}>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm" className={`${cls} p-1`} onClick={onClick}>{icon}</Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">{label}</TooltipContent>
                                  </Tooltip>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  currentPage={currentPage}
                  totalItems={filteredVendors.length}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={setItemsPerPage}
                  currentPageCount={paginatedVendors.length}
                  itemLabel="vendors"
                />
              </CardContent>
            </Card>

            {/* Add Vendor Dialog */}
            <Dialog open={showVendorForm} onOpenChange={(open) => { setShowVendorForm(open); if (!open) setVendorErrors({}); }}>
              <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">Vendor Registration</DialogTitle>
                </DialogHeader>
                {vendorErrors._form && (
                  <div className="mx-4 mb-4">
                    <Alert variant="destructive" className="bg-red-50 text-red-800 border-red-200 rounded-lg">
                      <AlertDescription className="font-medium">{vendorErrors._form}</AlertDescription>
                    </Alert>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="col-span-1 sm:col-span-2">
                    <Label htmlFor="vendor-name">Vendor Name*</Label>
                    <Input id="vendor-name" value={newVendor.name} onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })} className={vendorErrors.name ? "border-red-500" : ""} />
                    {vendorErrors.name && <p className="text-xs text-red-500 mt-1">{vendorErrors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="business-type">Business Type*</Label>
                    <Select value={newVendor.businessType} onValueChange={(value) => setNewVendor({ ...newVendor, businessType: value, otherBusinessType: "" })}>
                      <SelectTrigger id="business-type" className={vendorErrors.businessType && newVendor.businessType !== "Other" ? "border-red-500" : ""}><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="empty">Select type</SelectItem>
                        {BUSINESS_TYPES.map((type) => <SelectItem key={type} value={type || "empty"}>{type}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {vendorErrors.businessType && newVendor.businessType !== "Other" && <p className="text-xs text-red-500 mt-1">{vendorErrors.businessType}</p>}
                    {newVendor.businessType === "Other" && (
                      <div className="mt-2">
                        <Input
                          placeholder="Specify your business type"
                          value={newVendor.otherBusinessType || ""}
                          onChange={(e) => setNewVendor({ ...newVendor, otherBusinessType: e.target.value })}
                          className={vendorErrors.businessType ? "border-red-500" : ""}
                        />
                        {vendorErrors.businessType && <p className="text-xs text-red-500 mt-1">{vendorErrors.businessType}</p>}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="gst-id">GST/Tax ID</Label>
                    <Input id="gst-id" value={newVendor.gstId} onChange={(e) => setNewVendor({ ...newVendor, gstId: e.target.value })} className={vendorErrors.gstId ? "border-red-500" : ""} />
                    {vendorErrors.gstId && <p className="text-xs text-red-500 mt-1">{vendorErrors.gstId}</p>}
                  </div>
                </div>
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Contact Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" value={newVendor.contactDetails.phone} onChange={(e) => setNewVendor({ ...newVendor, contactDetails: { ...newVendor.contactDetails, phone: e.target.value } })} className={vendorErrors.phone ? "border-red-500" : ""} />
                      {vendorErrors.phone && <p className="text-xs text-red-500 mt-1">{vendorErrors.phone}</p>}
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" value={newVendor.contactDetails.email} onChange={(e) => setNewVendor({ ...newVendor, contactDetails: { ...newVendor.contactDetails, email: e.target.value } })} className={vendorErrors.email ? "border-red-500" : ""} />
                      {vendorErrors.email && <p className="text-xs text-red-500 mt-1">{vendorErrors.email}</p>}
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      <Label htmlFor="address">Address</Label>
                      <Textarea id="address" rows={2} value={newVendor.contactDetails.address} onChange={(e) => setNewVendor({ ...newVendor, contactDetails: { ...newVendor.contactDetails, address: e.target.value } })} />
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <h3 className="font-medium mb-2">Bank Details</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="account-name">Account Name</Label>
                      <Input id="account-name" value={newVendor.bankDetails?.accountName || ""} onChange={(e) => setNewVendor({ ...newVendor, bankDetails: { ...newVendor.bankDetails, accountName: e.target.value } })} className={vendorErrors.accountName ? "border-red-500" : ""} />
                      {vendorErrors.accountName && <p className="text-xs text-red-500 mt-1">{vendorErrors.accountName}</p>}
                    </div>
                    <div>
                      <Label htmlFor="account-number">Account Number</Label>
                      <Input id="account-number" value={newVendor.bankDetails?.accountNumber || ""} onChange={(e) => setNewVendor({ ...newVendor, bankDetails: { ...newVendor.bankDetails, accountNumber: e.target.value } })} className={vendorErrors.accountNumber ? "border-red-500" : ""} />
                      {vendorErrors.accountNumber && <p className="text-xs text-red-500 mt-1">{vendorErrors.accountNumber}</p>}
                    </div>
                    <div>
                      <Label htmlFor="ifsc-code">IFSC Code</Label>
                      <Input id="ifsc-code" value={newVendor.bankDetails?.ifscCode || ""} onChange={(e) => setNewVendor({ ...newVendor, bankDetails: { ...newVendor.bankDetails, ifscCode: e.target.value } })} className={vendorErrors.ifscCode ? "border-red-500" : ""} />
                      {vendorErrors.ifscCode && <p className="text-xs text-red-500 mt-1">{vendorErrors.ifscCode}</p>}
                    </div>
                    <div>
                      <Label htmlFor="bank-name">Bank Name</Label>
                      <Input id="bank-name" value={newVendor.bankDetails?.bankName || ""} onChange={(e) => setNewVendor({ ...newVendor, bankDetails: { ...newVendor.bankDetails, bankName: e.target.value } })} className={vendorErrors.bankName ? "border-red-500" : ""} />
                      {vendorErrors.bankName && <p className="text-xs text-red-500 mt-1">{vendorErrors.bankName}</p>}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowVendorForm(false); setVendorErrors({}); }}>Cancel</Button>
                  <Button className="bg-[#0263cc] hover:bg-[#0263cc]/90" onClick={handleAddVendor} disabled={loading}>
                    {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
                    Register Vendor
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit Vendor Dialog */}
            <Dialog open={showEditForm} onOpenChange={(open) => { setShowEditForm(open); if (!open) { setEditVendorErrors({}); setEditingVendor(null); } }}>
              <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">Edit Vendor</DialogTitle>
                </DialogHeader>
                {editingVendor && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div className="col-span-1 sm:col-span-2">
                        <Label htmlFor="edit-vendor-name">Vendor Name*</Label>
                        <Input id="edit-vendor-name" value={editingVendor.name} onChange={(e) => setEditingVendor({ ...editingVendor, name: e.target.value })} className={editVendorErrors.name ? "border-red-500" : ""} />
                        {editVendorErrors.name && <p className="text-xs text-red-500 mt-1">{editVendorErrors.name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="edit-business-type">Business Type*</Label>
                        <Select value={editingVendor.businessType} onValueChange={(value) => setEditingVendor({ ...editingVendor, businessType: value, otherBusinessType: "" })}>
                          <SelectTrigger id="edit-business-type" className={editVendorErrors.businessType && editingVendor.businessType !== "Other" ? "border-red-500" : ""}><SelectValue placeholder="Select type" /></SelectTrigger>
                          <SelectContent>
                            {BUSINESS_TYPES.map((type) => <SelectItem key={type} value={type || "empty"}>{type}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {editVendorErrors.businessType && editingVendor.businessType !== "Other" && <p className="text-xs text-red-500 mt-1">{editVendorErrors.businessType}</p>}
                        {editingVendor.businessType === "Other" && (
                          <div className="mt-2">
                            <Input
                              placeholder="Specify your business type"
                              value={editingVendor.otherBusinessType || ""}
                              onChange={(e) => setEditingVendor({ ...editingVendor, otherBusinessType: e.target.value })}
                              className={editVendorErrors.businessType ? "border-red-500" : ""}
                            />
                            {editVendorErrors.businessType && <p className="text-xs text-red-500 mt-1">{editVendorErrors.businessType}</p>}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="edit-gst-id">GST/Tax ID</Label>
                        <Input id="edit-gst-id" value={editingVendor.gstId} onChange={(e) => setEditingVendor({ ...editingVendor, gstId: e.target.value })} className={editVendorErrors.gstId ? "border-red-500" : ""} />
                        {editVendorErrors.gstId && <p className="text-xs text-red-500 mt-1">{editVendorErrors.gstId}</p>}
                      </div>
                    </div>
                    <div className="mb-4">
                      <h3 className="font-medium mb-2">Contact Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-phone">Phone</Label>
                          <Input id="edit-phone" value={editingVendor.contactDetails.phone} onChange={(e) => setEditingVendor({ ...editingVendor, contactDetails: { ...editingVendor.contactDetails, phone: e.target.value } })} className={editVendorErrors.phone ? "border-red-500" : ""} />
                          {editVendorErrors.phone && <p className="text-xs text-red-500 mt-1">{editVendorErrors.phone}</p>}
                        </div>
                        <div>
                          <Label htmlFor="edit-email">Email</Label>
                          <Input id="edit-email" type="email" value={editingVendor.contactDetails.email} onChange={(e) => setEditingVendor({ ...editingVendor, contactDetails: { ...editingVendor.contactDetails, email: e.target.value } })} className={editVendorErrors.email ? "border-red-500" : ""} />
                          {editVendorErrors.email && <p className="text-xs text-red-500 mt-1">{editVendorErrors.email}</p>}
                        </div>
                        <div className="col-span-1 sm:col-span-2">
                          <Label htmlFor="edit-address">Address</Label>
                          <Textarea id="edit-address" rows={2} value={editingVendor.contactDetails.address} onChange={(e) => setEditingVendor({ ...editingVendor, contactDetails: { ...editingVendor.contactDetails, address: e.target.value } })} />
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <h3 className="font-medium mb-2">Bank Details</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="edit-account-name">Account Name</Label>
                          <Input id="edit-account-name" value={editingVendor.bankDetails?.accountName || ""} onChange={(e) => setEditingVendor({ ...editingVendor, bankDetails: { ...editingVendor.bankDetails, accountName: e.target.value } })} className={editVendorErrors.accountName ? "border-red-500" : ""} />
                          {editVendorErrors.accountName && <p className="text-xs text-red-500 mt-1">{editVendorErrors.accountName}</p>}
                        </div>
                        <div>
                          <Label htmlFor="edit-account-number">Account Number</Label>
                          <Input id="edit-account-number" value={editingVendor.bankDetails?.accountNumber || ""} onChange={(e) => setEditingVendor({ ...editingVendor, bankDetails: { ...editingVendor.bankDetails, accountNumber: e.target.value } })} className={editVendorErrors.accountNumber ? "border-red-500" : ""} />
                          {editVendorErrors.accountNumber && <p className="text-xs text-red-500 mt-1">{editVendorErrors.accountNumber}</p>}
                        </div>
                        <div>
                          <Label htmlFor="edit-ifsc-code">IFSC Code</Label>
                          <Input id="edit-ifsc-code" value={editingVendor.bankDetails?.ifscCode || ""} onChange={(e) => setEditingVendor({ ...editingVendor, bankDetails: { ...editingVendor.bankDetails, ifscCode: e.target.value } })} className={editVendorErrors.ifscCode ? "border-red-500" : ""} />
                          {editVendorErrors.ifscCode && <p className="text-xs text-red-500 mt-1">{editVendorErrors.ifscCode}</p>}
                        </div>
                        <div>
                          <Label htmlFor="edit-bank-name">Bank Name</Label>
                          <Input id="edit-bank-name" value={editingVendor.bankDetails?.bankName || ""} onChange={(e) => setEditingVendor({ ...editingVendor, bankDetails: { ...editingVendor.bankDetails, bankName: e.target.value } })} className={editVendorErrors.bankName ? "border-red-500" : ""} />
                          {editVendorErrors.bankName && <p className="text-xs text-red-500 mt-1">{editVendorErrors.bankName}</p>}
                        </div>
                      </div>
                    </div>
                    <div className="mb-4">
                      <Label htmlFor="edit-agreement-url">Agreement / Contract Document</Label>
                      <div className="flex gap-2 mt-1">
                        <Input id="edit-agreement-url" type="url" placeholder="https://drive.google.com/… or any direct PDF link" value={editingVendor.agreementFile || ""} onChange={(e) => setEditingVendor({ ...editingVendor, agreementFile: e.target.value })} className="flex-1" />
                        {editingVendor.agreementFile?.trim() && (
                          <Button type="button" variant="outline" size="sm" onClick={() => window.open(editingVendor.agreementFile, "_blank", "noopener,noreferrer")} className="shrink-0 flex items-center gap-1">
                            <ExternalLink className="h-3 w-3" /> Preview
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Upload your PDF to <strong>Google Drive</strong>, <strong>Dropbox</strong>, or any file host, then paste the share link here. The document icon on the vendor row will open it.
                      </p>
                    </div>
                  </>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setShowEditForm(false); setEditingVendor(null); setEditVendorErrors({}); }}>Cancel</Button>
                  <Button className="bg-[#0263cc] hover:bg-[#0263cc]/90" onClick={handleEditVendor} disabled={loading}>
                    {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Add Transaction Dialog */}
            <Dialog open={showTransactionForm} onOpenChange={(open) => { setShowTransactionForm(open); if (!open) setTxErrors({}); }}>
              <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">Add New Transaction</DialogTitle>
                  <DialogDescription>
                    Record a purchase, payment, or delivery against a vendor for your records. Actual ordering is done outside the app.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 gap-4 mb-4">
                  <div>
                    <Label htmlFor="transaction-vendor">Vendor*</Label>
                    <Select
                      value={selectedVendorForTransaction?.id != null ? String(selectedVendorForTransaction.id) : ""}
                      onValueChange={(value) => { const vendor = vendors.find((v) => String(v.id) === value); setSelectedVendorForTransaction(vendor || null); setTxErrors((prev) => ({ ...prev, vendor: null })); }}
                    >
                      <SelectTrigger id="transaction-vendor" className={txErrors.vendor ? "border-red-500" : ""}><SelectValue placeholder="Select vendor" /></SelectTrigger>
                      <SelectContent>
                        {vendors.filter((v) => v.status === "active" || selectedVendorForTransaction?.id === v.id).map((vendor) => (
                          <SelectItem key={vendor.id} value={String(vendor.id)}>
                            {vendor.name}{vendor.status !== "active" ? " (inactive)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {txErrors.vendor && <p className="text-red-500 text-xs mt-1">{txErrors.vendor}</p>}
                  </div>
                  <div>
                    <Label htmlFor="transaction-id">Transaction ID</Label>
                    <Input id="transaction-id" value={transactionId} readOnly className="bg-gray-100" />
                  </div>
                  <div>
                    <Label htmlFor="transaction-date">Date</Label>
                    <Input id="transaction-date" type="date" value={newTransaction.date} onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="transaction-amount">Amount (₹)*</Label>
                    <Input id="transaction-amount" type="number" value={newTransaction.amount} onChange={(e) => { setNewTransaction({ ...newTransaction, amount: parseFloat(e.target.value) || 0 }); setTxErrors((prev) => ({ ...prev, amount: null })); }} onFocus={(e) => e.target.select()} className={txErrors.amount ? "border-red-500" : ""} min={1} />
                    {txErrors.amount && <p className="text-red-500 text-xs mt-1">{txErrors.amount}</p>}
                  </div>
                  <div>
                    <Label htmlFor="transaction-status">Payment Status</Label>
                    <Select value={newTransaction.status} onValueChange={(value) => setNewTransaction({ ...newTransaction, status: value, paid_amount: 0 })}>
                      <SelectTrigger id="transaction-status"><SelectValue placeholder="Payment Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="partial">Partially Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newTransaction.status === "partial" && (
                    <div>
                      <Label htmlFor="transaction-paid-amount">Amount Already Paid (₹)*</Label>
                      <Input id="transaction-paid-amount" type="number" value={newTransaction.paid_amount} onChange={(e) => { setNewTransaction({ ...newTransaction, paid_amount: parseFloat(e.target.value) || 0 }); setTxErrors((prev) => ({ ...prev, paid_amount: null })); }} onFocus={(e) => e.target.select()} className={txErrors.paid_amount ? "border-red-500" : ""} min={1} />
                      {txErrors.paid_amount && <p className="text-red-500 text-xs mt-1">{txErrors.paid_amount}</p>}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="deliveryOnTime"
                      checked={newTransaction.deliveryOnTime}
                      onCheckedChange={(checked) => setNewTransaction({ ...newTransaction, deliveryOnTime: checked === true })}
                    />
                    <Label htmlFor="deliveryOnTime">Delivery was on time</Label>
                  </div>
                  <div>
                    <Label htmlFor="transaction-notes">Notes</Label>
                    <Textarea id="transaction-notes" rows={2} value={newTransaction.notes} onChange={(e) => setNewTransaction({ ...newTransaction, notes: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowTransactionForm(false)}>Cancel</Button>
                  <Button className="bg-green-600 hover:bg-green-700" onClick={() => { const errs = {}; if (!selectedVendorForTransaction) errs.vendor = "Please select a vendor"; if (!newTransaction.amount || newTransaction.amount <= 0) errs.amount = "Amount must be greater than ₹0"; if (newTransaction.status === "partial") { if (!newTransaction.paid_amount || newTransaction.paid_amount <= 0) errs.paid_amount = "Please enter amount already paid"; else if (newTransaction.paid_amount >= newTransaction.amount) errs.paid_amount = "Paid amount must be less than total amount"; } if (Object.keys(errs).length) { setTxErrors(errs); return; } handleAddTransaction(); }} disabled={loading}>
                    {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
                    Add Transaction
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Transactions Modal */}
            <Dialog open={showTxModal} onOpenChange={setShowTxModal}>
              <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">{txModalVendor?.name} — Transactions</DialogTitle>
                  <DialogDescription>View, edit, and manage all transactions for this vendor.</DialogDescription>
                </DialogHeader>
                <div className="flex items-center gap-2 mb-2">
                  <Label className="shrink-0">Filter:</Label>
                  <Select value={txFilter} onValueChange={setTxFilter}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground ml-auto">{filteredTxList.length} result{filteredTxList.length !== 1 ? "s" : ""}</span>
                </div>
                <div className="overflow-y-auto flex-1 -mx-6 px-6">
                  {filteredTxList.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">No transactions found.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="border-b text-left text-xs font-semibold text-gray-500 uppercase">
                          <th className="pb-2 pr-3">ID</th>
                          <th className="pb-2 pr-3">Date</th>
                          <th className="pb-2 pr-3">Amount</th>
                          <th className="pb-2 pr-3">Status</th>
                          <th className="pb-2 pr-3">Delivery</th>
                          <th className="pb-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {filteredTxList.map((t) => (
                          <tr key={t.id} className="hover:bg-gray-50">
                            <td className="py-2 pr-3 font-mono text-xs">{t.id}</td>
                            <td className="py-2 pr-3">{new Date(t.date).toLocaleDateString()}</td>
                            <td className="py-2 pr-3 font-medium">₹{t.amount?.toLocaleString()}</td>
                            <td className="py-2 pr-3"><span className={getStatusBadgeClass(t.status)}>{t.status}</span></td>
                            <td className="py-2 pr-3"><span className={getStatusBadgeClass(t.deliveryOnTime ? "active" : "inactive")}>{t.deliveryOnTime ? "On-time" : "Delayed"}</span></td>
                            <td className="py-2">
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-[#0263cc]" onClick={() => { setEditingTransaction({ vendorId: txModalVendorId, ...t }); setShowEditTransactionForm(true); }}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                {t.status === "pending" && (
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-green-700 text-xs" onClick={() => updateTransactionStatus(txModalVendorId, t.id, "paid")}>
                                    Mark Paid
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="border-t pt-3 mt-2 flex justify-between items-center text-sm text-gray-600">
                  <div>
                    Total: ₹{(txModalVendor?.transactions || []).reduce((s, t) => s + (t.amount || 0), 0).toLocaleString()}
                    {" · "}
                    <span className="text-red-600">Pending: ₹{(txModalVendor?.transactions || []).filter(t => t.status === "pending").reduce((s, t) => s + (t.amount || 0), 0).toLocaleString()}</span>
                    {" · "}
                    <span className="text-yellow-600">Partial: ₹{(txModalVendor?.transactions || []).filter(t => t.status === "partial").reduce((s, t) => s + (t.amount || 0), 0).toLocaleString()}</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowTxModal(false)}>Close</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Transaction Dialog */}
            <Dialog open={showEditTransactionForm} onOpenChange={(open) => { setShowEditTransactionForm(open); if (!open) setEditingTransaction(null); }}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">Edit Transaction</DialogTitle>
                </DialogHeader>
                {editingTransaction && (
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={editingTransaction.date?.split("T")[0] || ""} onChange={(e) => setEditingTransaction({ ...editingTransaction, date: e.target.value })} />
                    </div>
                    <div>
                      <Label>Amount (₹)</Label>
                      <Input type="number" value={editingTransaction.amount || 0} onChange={(e) => setEditingTransaction({ ...editingTransaction, amount: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} />
                    </div>
                    <div>
                      <Label>Payment Status</Label>
                      <Select value={editingTransaction.status} onValueChange={(v) => setEditingTransaction({ ...editingTransaction, status: v, paid_amount: 0 })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="partial">Partially Paid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {editingTransaction.status === "partial" && (
                      <div>
                        <Label>Amount Already Paid (₹)*</Label>
                        <Input type="number" value={editingTransaction.paid_amount || 0} onChange={(e) => setEditingTransaction({ ...editingTransaction, paid_amount: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} min={1} />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Checkbox id="edit-delivery" checked={editingTransaction.deliveryOnTime} onCheckedChange={(c) => setEditingTransaction({ ...editingTransaction, deliveryOnTime: c === true })} />
                      <Label htmlFor="edit-delivery">Delivery was on time</Label>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea rows={2} value={editingTransaction.notes || ""} onChange={(e) => setEditingTransaction({ ...editingTransaction, notes: e.target.value })} />
                    </div>
                  </div>
                )}
                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={() => { setShowEditTransactionForm(false); setEditingTransaction(null); }}>Cancel</Button>
                  <Button className="bg-[#0263cc] hover:bg-[#0263cc]/90" onClick={() => updateTransaction(editingTransaction.vendorId, editingTransaction.id, editingTransaction)} disabled={loading}>
                    {loading && <Clock className="mr-2 h-4 w-4 animate-spin" />}
                    Save Changes
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Confirm Dialog (unsettled payments) */}
            <Dialog open={!!deleteConfirmVendor} onOpenChange={(open) => { if (!open) setDeleteConfirmVendor(null); }}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold flex items-center gap-2 text-red-600">
                    <AlertTriangle className="h-5 w-5" /> Unsettled Payments
                  </DialogTitle>
                  <DialogDescription className="pt-2">
                    <strong>{deleteConfirmVendor?.vendor?.name}</strong> has{" "}
                    <strong className="text-red-600">₹{deleteConfirmVendor?.unsettled?.toLocaleString()}</strong> in unsettled payments (pending or partial). Deleting will permanently remove all vendor data and transaction history.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="mt-2">
                  <Button variant="outline" onClick={() => setDeleteConfirmVendor(null)}>Cancel</Button>
                  <Button variant="destructive" onClick={() => initiateDelete(deleteConfirmVendor.vendor)}>Delete Anyway</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <VendorReportsTab
              vendors={vendors}
              getReportData={getReportData}
              getAlerts={getAlerts}
              getVendorPerformance={getVendorPerformance}
              getStatusBadgeClass={getStatusBadgeClass}
            />
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
};

export default VendorManagement;
